<template>
  <section class="print-engine-preview" aria-live="polite">
    <header class="preview-heading">
      <div class="preview-title">
        <span class="preview-icon"><i class="fas fa-print"></i></span>
        <div>
          <span class="etq-panel-kicker">Conferência fiel</span>
          <h6>Prévia do motor de impressão</h6>
          <p>
            Este quadro usa o mesmo desenho processado pelo servidor que gera o
            PDF.
          </p>
        </div>
      </div>

      <div class="preview-actions">
        <div class="quantity-control">
          <label for="etiqueta-preview-quantity">
            <strong>Quantidade</strong>
            <small>Vale também para a impressão teste</small>
          </label>
          <div class="quantity-input">
            <button
              type="button"
              aria-label="Diminuir quantidade"
              :disabled="loading || quantity <= 1"
              @click="changeQuantity(quantity - 1)"
            >
              <i class="fas fa-minus"></i>
            </button>
            <input
              id="etiqueta-preview-quantity"
              type="number"
              min="1"
              :max="maxQuantity"
              :value="quantity"
              :disabled="loading"
              @change="changeQuantity($event.target.value)"
            />
            <button
              type="button"
              aria-label="Aumentar quantidade"
              :disabled="loading || quantity >= maxQuantity"
              @click="changeQuantity(quantity + 1)"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <button
          type="button"
          class="generate-preview-button btn btn-primary font-weight-bold"
          :class="{ 'spinner spinner-light spinner-right': loading }"
          :disabled="loading"
          @click="$emit('generate')"
        >
          <i v-if="!loading" class="fas fa-file-pdf mr-2"></i>
          {{ loading ? "Gerando prévia..." : generateLabel }}
        </button>
      </div>
    </header>

    <div class="preview-state-bar">
      <span v-if="loading" class="preview-status loading">
        <i class="fas fa-circle-notch fa-spin"></i> Gerando agora
      </span>
      <span v-else-if="error" class="preview-status error">
        <i class="fas fa-exclamation-circle"></i> Falha ao atualizar
      </span>
      <span v-else-if="outdated" class="preview-status outdated">
        <i class="fas fa-clock"></i> Prévia desatualizada
      </span>
      <span v-else-if="pdfUrl" class="preview-status ready">
        <i class="fas fa-check-circle"></i> Prévia atualizada
      </span>
      <span v-else class="preview-status idle">
        <i class="far fa-file-pdf"></i> Prévia ainda não gerada
      </span>
      <small v-if="outdated && !loading"
        >Clique em <strong>Atualizar prévia</strong> para aplicar as últimas
        alterações.</small
      >
      <small v-else-if="!pdfUrl && !loading"
        >A geração é manual para deixar a edição mais rápida.</small
      >
    </div>

    <div class="preview-stage">
      <object
        v-if="pdfUrl"
        :key="revision"
        class="preview-frame"
        :data="pdfUrl"
        type="application/pdf"
        aria-label="PDF real da etiqueta gerado pelo motor de impressão"
      >
        <div class="preview-empty error">
          <i class="fas fa-external-link-alt"></i>
          <strong>Seu navegador não exibiu o PDF neste quadro</strong>
          <a :href="pdfUrl" target="_blank" rel="noopener noreferrer"
            >Abrir a prévia em outra guia</a
          >
        </div>
      </object>

      <div v-else-if="loading" class="preview-empty">
        <span class="spinner spinner-primary spinner-lg"></span>
        <strong>Preparando a prévia fiel...</strong>
        <small>O canvas continua disponível para edição.</small>
      </div>

      <div v-else-if="error" class="preview-empty error">
        <i class="fas fa-exclamation-triangle"></i>
        <strong>Não foi possível gerar a prévia</strong>
        <small>{{ error }}</small>
        <button
          type="button"
          class="btn btn-sm btn-light-danger font-weight-bold"
          @click="$emit('generate')"
        >
          <i class="fas fa-redo mr-1"></i>Tentar novamente
        </button>
      </div>

      <div v-else class="preview-empty">
        <i class="far fa-file-image"></i>
        <strong>A prévia aparecerá aqui</strong>
        <small>Faça uma alteração ou aguarde o carregamento do modelo.</small>
      </div>

      <div v-if="loading && pdfUrl" class="preview-updating">
        <i class="fas fa-circle-notch fa-spin"></i>
        Atualizando conforme suas alterações
      </div>
    </div>

    <div v-if="error && pdfUrl" class="preview-message error">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ error }}</span>
      <button
        type="button"
        class="btn btn-xs btn-light-danger font-weight-bold"
        @click="$emit('generate')"
      >
        Tentar novamente
      </button>
    </div>

    <div v-if="warnings.length" class="preview-message warning">
      <i class="fas fa-ruler-combined"></i>
      <div>
        <strong>Confira as medidas</strong>
        <span v-for="warning in warnings" :key="warning">{{ warning }}</span>
      </div>
    </div>

    <footer>
      <i class="fas fa-info-circle"></i>
      Este é o PDF real do motor. Ele só muda quando você clica em
      <strong>Gerar/Atualizar prévia</strong>; ainda assim, use
      <strong>Impressão teste</strong> antes de publicar.
    </footer>
  </section>
</template>

<script>
export default {
  name: "EtiquetaPrintEnginePreview",
  props: {
    pdfUrl: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
    outdated: {
      type: Boolean,
      default: false,
    },
    quantity: {
      type: Number,
      default: 1,
    },
    maxQuantity: {
      type: Number,
      default: 500,
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    revision: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    generateLabel() {
      return this.pdfUrl ? "Atualizar prévia" : "Gerar prévia";
    },
  },
  methods: {
    changeQuantity(value) {
      const parsed = Number.parseInt(value, 10);
      const next = Math.min(
        this.maxQuantity,
        Math.max(1, Number.isNaN(parsed) ? 1 : parsed)
      );
      this.$emit("update:quantity", next);
    },
  },
};
</script>

<style lang="scss" scoped>
.print-engine-preview {
  margin-top: 14px;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(31, 45, 61, 0.04);
}

.preview-heading {
  min-height: 86px;
  padding: 13px 15px;
  border-bottom: 1px solid #e4e6ef;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.preview-actions {
  flex: 1 1 260px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
}

.quantity-control {
  label {
    margin: 0 0 4px;

    strong,
    small {
      display: block;
    }

    strong {
      color: #5e6278;
      font-size: 9px;
    }

    small {
      color: #a1a5b7;
      font-size: 7px;
    }
  }
}

.quantity-input {
  height: 34px;
  border: 1px solid #d8dbe5;
  border-radius: 7px;
  display: flex;
  overflow: hidden;

  button {
    width: 29px;
    border: 0;
    background: #f3f6f9;
    color: #7e8299;
    font-size: 8px;

    &:hover:not(:disabled) {
      background: #eee9ff;
      color: #8950fc;
    }
  }

  input {
    width: 52px;
    border: 0;
    border-right: 1px solid #e4e6ef;
    border-left: 1px solid #e4e6ef;
    color: #3f4254;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      margin: 0;
      appearance: none;
    }
  }
}

.generate-preview-button {
  min-width: 145px;
  height: 34px;
  font-size: 10px;
}

.preview-state-bar {
  min-height: 37px;
  padding: 7px 15px;
  border-bottom: 1px solid #e4e6ef;
  background: #fbfbfd;
  display: flex;
  align-items: center;
  gap: 9px;

  small {
    color: #7e8299;
    font-size: 8px;
  }
}

.preview-title {
  min-width: 0;
  flex: 1 1 260px;
  display: flex;
  align-items: center;
  gap: 11px;

  h6 {
    margin: 1px 0 2px;
    color: #3f4254;
    font-size: 13px;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: #7e8299;
    font-size: 9px;
  }
}

.preview-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 9px;
  background: #e8fff3;
  color: #169f6c;
  display: grid;
  place-items: center;
  font-size: 14px;
}

.etq-panel-kicker {
  color: #7e8299;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.preview-status {
  flex: 0 0 auto;
  padding: 5px 8px;
  border-radius: 13px;
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;

  i {
    margin-right: 3px;
  }

  &.loading {
    background: #eee9ff;
    color: #8950fc;
  }

  &.ready {
    background: #e8fff3;
    color: #169f6c;
  }

  &.error {
    background: #ffe2e5;
    color: #f64e60;
  }

  &.outdated {
    background: #fff4de;
    color: #b76e00;
  }

  &.idle {
    background: #f3f6f9;
    color: #7e8299;
  }
}

.preview-stage {
  position: relative;
  min-height: 330px;
  background-color: #eef1f5;
  background-image: radial-gradient(#d3d7e1 0.7px, transparent 0.7px);
  background-size: 14px 14px;
}

.preview-frame {
  width: 100%;
  height: 380px;
  border: 0;
  display: block;
  background: #eef1f5;
}

.preview-empty {
  min-height: 330px;
  padding: 30px;
  color: #a1a5b7;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 7px;

  > i {
    margin-bottom: 4px;
    font-size: 27px;
  }

  strong {
    color: #5e6278;
    font-size: 11px;
  }

  small {
    max-width: 360px;
    font-size: 9px;
  }

  .btn {
    margin-top: 5px;
  }

  &.error > i,
  &.error strong {
    color: #f64e60;
  }
}

.preview-updating {
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 6px 9px;
  border: 1px solid rgba(137, 80, 252, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  color: #8950fc;
  font-size: 8px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.08);

  i {
    margin-right: 4px;
  }
}

.preview-message {
  margin: 10px 12px 0;
  padding: 9px 10px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 9px;

  strong,
  span {
    display: block;
  }

  span + span {
    margin-top: 2px;
  }

  .btn {
    flex: 0 0 auto;
    margin-left: auto;
  }

  &.warning {
    background: #fff4de;
    color: #9d650d;
  }

  &.error {
    align-items: center;
    background: #ffe2e5;
    color: #c93648;
  }
}

footer {
  padding: 11px 14px;
  color: #7e8299;
  font-size: 8px;

  i {
    margin-right: 4px;
    color: #3699ff;
  }

  strong {
    color: #5e6278;
  }
}

@media (max-width: 700px) {
  .preview-heading {
    align-items: flex-start;
  }

  .preview-title p {
    display: none;
  }

  .preview-frame {
    height: 330px;
  }
}
</style>
